import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getonepages, savepages } from "../Reducers/actions";
import { message } from "../../../Reducers/loading/actions";
import moment from "moment";
import ContentBuilder from "./Components/contentbuilder";
import SneaTabs from "../../../Components/SneaTabs/SneaTabs";

const ModernContentEditor = ({ page, settings, getonepages, savepages, message }) => {
  const [buildElements, setBuildElements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taal, setTaal] = useState("");
  const [activeTab, setActiveTab] = useState("content");
  const [pageSettings, setPageSettings] = useState({
    title: "",
    description: "",
    keywords: "",
    status: "draft",
    publishDate: "",
    featured: false
  });
  const [showPreview, setShowPreview] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    const queryParams = new URLSearchParams(location.search);
    const taalkeuze = queryParams.get("taal") || "";
    setTaal(taalkeuze);
    
    getonepages(params.id).then((res) => {
      if (res.type === "GET_CMS_PAGE") {
        const pageData = res.results;
        
        if (pageData.type === "page") {
          setBuildElements(pageData.pagetemmplate?.content || []);
        } else {
          setBuildElements(pageData.components || []);
        }
        
        // Set page settings
        setPageSettings({
          title: pageData.title || pageData.naam || "",
          description: pageData.description || "",
          keywords: pageData.keywords || "",
          status: pageData.status || "draft",
          publishDate: pageData.publishDate || "",
          featured: pageData.featured || false
        });
        
        setLoading(false);
      }
    });
  }, [location.search, params.id]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const result = await savepages({
        ...values,
        ...pageSettings,
        _id: params.id
      });
      
      if (result.type === "GET_CMS_PAGE") {
        message(3000, "success", "Content succesvol opgeslagen");
        setUnsavedChanges(false);
      } else {
        message(4000, "danger", "Er is een fout opgetreden");
      }
    } catch (error) {
      message(4000, "danger", "Er is een fout opgetreden bij het opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPublish = async () => {
    const updatedSettings = { ...pageSettings, status: "published", publishDate: new Date() };
    setPageSettings(updatedSettings);
    await handleSubmit({ ...buildElements, ...updatedSettings });
  };

  const handleLanguageChange = (selectedLanguage) => {
    setTaal(selectedLanguage);
    const selectedLanguageObj = settings.talen?.find((lang) => lang.taal === selectedLanguage);
    const newUrl = selectedLanguageObj?.default
      ? `/cms/content/${params.id}`
      : `/cms/content/${params.id}?taal=${selectedLanguage}`;
    navigate(newUrl);
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: { class: "success", icon: "fa-check-circle", text: "Gepubliceerd" },
      draft: { class: "warning", icon: "fa-edit", text: "Concept" },
      archived: { class: "secondary", icon: "fa-archive", text: "Gearchiveerd" },
      scheduled: { class: "info", icon: "fa-clock", text: "Ingepland" }
    };
    return badges[status] || badges.draft;
  };

  if (loading) {
    return (
      <div className="modern-content-editor">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <h5 className="text-muted">Content laden...</h5>
          </div>
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(pageSettings.status);

  return (
    <div className="modern-content-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center py-3">
            <div className="d-flex align-items-center">
              <button 
                className="btn btn-outline-secondary me-3"
                onClick={() => navigate('/cms/content')}
              >
                <i className="fa fa-arrow-left me-2"></i>
                Terug
              </button>
              <div>
                <h1 className="h3 mb-1">{pageSettings.title || page?.naam || 'Nieuwe Content'}</h1>
                <div className="d-flex align-items-center gap-3">
                  <span className={`badge bg-${statusBadge.class}`}>
                    <i className={`fa ${statusBadge.icon} me-1`}></i>
                    {statusBadge.text}
                  </span>
                  {unsavedChanges && (
                    <span className="badge bg-warning">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      Niet opgeslagen
                    </span>
                  )}
                  <small className="text-muted">
                    Laatst bewerkt: {moment(page?.updated).fromNow()}
                  </small>
                </div>
              </div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={() => setShowPreview(!showPreview)}
              >
                <i className={`fa ${showPreview ? 'fa-edit' : 'fa-eye'} me-2`}></i>
                {showPreview ? 'Bewerken' : 'Voorvertoning'}
              </button>
              
              {pageSettings.status === 'draft' && (
                <button 
                  className="btn btn-success"
                  onClick={handleSaveAndPublish}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Publiceren...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-globe me-2"></i>
                      Publiceren
                    </>
                  )}
                </button>
              )}
              
              <button 
                className="btn btn-primary"
                onClick={() => handleSubmit(buildElements)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Opslaan...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-2"></i>
                    Opslaan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid">
        <div className="row">
          {/* Main Editor */}
          <div className="col-lg-9">
            <div className="editor-content">
              {/* Tabs */}
              <SneaTabs
                tabs={[
                  {
                    id: 'content',
                    label: (
                      <span>
                        <i className="fa fa-edit me-2"></i>
                        Content
                      </span>
                    ),
                    content: (
                      <div className="content-builder-wrapper">
                        {showPreview ? (
                          <div className="preview-mode">
                            <div className="alert alert-info">
                              <i className="fa fa-info-circle me-2"></i>
                              Voorvertoning modus - zo ziet je content eruit voor bezoekers
                            </div>
                            {/* Preview content would go here */}
                            <div className="preview-content">
                              {/* This would be the rendered content */}
                              <p className="text-muted">Voorvertoning wordt geladen...</p>
                            </div>
                          </div>
                        ) : (
                          buildElements.length > 0 && (
                            <ContentBuilder
                              settings={settings}
                              buildElements={buildElements}
                              onSubmit={handleSubmit}
                              taal={taal}
                              onChange={() => setUnsavedChanges(true)}
                            />
                          )
                        )}
                      </div>
                    )
                  },
                  {
                    id: 'settings',
                    label: (
                      <span>
                        <i className="fa fa-cog me-2"></i>
                        Instellingen
                      </span>
                    ),
                    content: (
                      <div className="settings-panel">
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <label className="form-label">Titel</label>
                              <input
                                type="text"
                                className="form-control"
                                value={pageSettings.title}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, title: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                                placeholder="Voer een titel in..."
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Beschrijving</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={pageSettings.description}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, description: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                                placeholder="Korte beschrijving van de content..."
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <select
                                className="form-select"
                                value={pageSettings.status}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, status: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                              >
                                <option value="draft">Concept</option>
                                <option value="published">Gepubliceerd</option>
                                <option value="scheduled">Ingepland</option>
                                <option value="archived">Gearchiveerd</option>
                              </select>
                            </div>

                            {pageSettings.status === 'scheduled' && (
                              <div className="mb-3">
                                <label className="form-label">Publicatie datum</label>
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={pageSettings.publishDate}
                                  onChange={(e) => {
                                    setPageSettings(prev => ({ ...prev, publishDate: e.target.value }));
                                    setUnsavedChanges(true);
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="col-md-4">
                            <div className="card bg-light">
                              <div className="card-body">
                                <h6 className="card-title">
                                  <i className="fa fa-lightbulb me-2"></i>
                                  Tips
                                </h6>
                                <ul className="list-unstyled small mb-0">
                                  <li className="mb-2">
                                    <i className="fa fa-check text-success me-2"></i>
                                    Gebruik duidelijke titels voor betere SEO
                                  </li>
                                  <li className="mb-2">
                                    <i className="fa fa-check text-success me-2"></i>
                                    Schrijf beschrijvende meta descriptions
                                  </li>
                                  <li className="mb-0">
                                    <i className="fa fa-check text-success me-2"></i>
                                    Test je content in voorvertoning modus
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  },
                  {
                    id: 'seo',
                    label: (
                      <span>
                        <i className="fa fa-search me-2"></i>
                        SEO
                      </span>
                    ),
                    content: (
                      <div className="seo-panel">
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <label className="form-label">SEO Titel</label>
                              <input
                                type="text"
                                className="form-control"
                                value={pageSettings.title}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, title: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                                placeholder="SEO geoptimaliseerde titel..."
                              />
                              <div className="form-text">
                                <span className={pageSettings.title.length > 60 ? 'text-danger' : 'text-success'}>
                                  {pageSettings.title.length}/60 karakters
                                </span>
                              </div>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Meta Beschrijving</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={pageSettings.description}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, description: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                                placeholder="Beschrijving die verschijnt in zoekresultaten..."
                              />
                              <div className="form-text">
                                <span className={pageSettings.description.length > 160 ? 'text-danger' : 'text-success'}>
                                  {pageSettings.description.length}/160 karakters
                                </span>
                              </div>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Keywords</label>
                              <input
                                type="text"
                                className="form-control"
                                value={pageSettings.keywords}
                                onChange={(e) => {
                                  setPageSettings(prev => ({ ...prev, keywords: e.target.value }));
                                  setUnsavedChanges(true);
                                }}
                                placeholder="keyword1, keyword2, keyword3..."
                              />
                              <div className="form-text">
                                Scheid keywords met komma's
                              </div>
                            </div>

                            {/* SEO Preview */}
                            <div className="card bg-light">
                              <div className="card-header">
                                <h6 className="mb-0">
                                  <i className="fa fa-google me-2"></i>
                                  Google Preview
                                </h6>
                              </div>
                              <div className="card-body">
                                <div className="seo-preview">
                                  <h6 className="text-primary mb-1">{pageSettings.title || "Titel van je pagina"}</h6>
                                  <div className="small text-success mb-1">www.jouwwebsite.nl/pagina</div>
                                  <p className="small mb-0 text-muted">
                                    {pageSettings.description || "Je meta beschrijving verschijnt hier. Maak het aantrekkelijk voor bezoekers!"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="card bg-light">
                              <div className="card-body">
                                <h6 className="card-title">
                                  <i className="fa fa-search me-2"></i>
                                  SEO Score
                                </h6>
                                <div className="seo-score mb-3">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>Titel lengte</span>
                                    <span className={`badge ${pageSettings.title.length >= 30 && pageSettings.title.length <= 60 ? 'bg-success' : 'bg-warning'}`}>
                                      {pageSettings.title.length >= 30 && pageSettings.title.length <= 60 ? 'Goed' : 'Te verbeteren'}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>Meta beschrijving</span>
                                    <span className={`badge ${pageSettings.description.length >= 120 && pageSettings.description.length <= 160 ? 'bg-success' : 'bg-warning'}`}>
                                      {pageSettings.description.length >= 120 && pageSettings.description.length <= 160 ? 'Goed' : 'Te verbeteren'}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>Keywords</span>
                                    <span className={`badge ${pageSettings.keywords.length > 0 ? 'bg-success' : 'bg-warning'}`}>
                                      {pageSettings.keywords.length > 0 ? 'Goed' : 'Ontbreekt'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  }
                ]}
                defaultActiveTab={activeTab === 'content' ? 0 : activeTab === 'settings' ? 1 : 2}
                onTabChange={(tabId, index) => {
                  const tabMap = ['content', 'settings', 'seo'];
                  setActiveTab(tabMap[index]);
                }}
              />
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="col-lg-3">
            <div className="editor-sidebar">
              {/* Language Selector */}
              {settings?.talen && settings.talen.length > 1 && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="fa fa-globe me-2"></i>
                      Talen
                    </h6>
                  </div>
                  <div className="card-body">
                    <select 
                      className="form-select"
                      value={taal}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      {settings.talen.map((item, index) => (
                        <option key={index} value={item.taal}>
                          {item.taal}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {/* Page Info */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fa fa-info-circle me-2"></i>
                    Informatie
                  </h6>
                </div>
                <div className="card-body">
                  <div className="info-item mb-3">
                    <small className="text-muted">Aangemaakt</small>
                    <div className="fw-semibold">
                      {moment(page?.date).format("DD MMM YYYY HH:mm")}
                    </div>
                  </div>
                  <div className="info-item mb-3">
                    <small className="text-muted">Laatst gewijzigd</small>
                    <div className="fw-semibold">
                      {moment(page?.updated).format("DD MMM YYYY HH:mm")}
                    </div>
                  </div>
                  <div className="info-item">
                    <small className="text-muted">Type</small>
                    <div className="fw-semibold">
                      <i className={`fa ${page?.pageType === 'pagina' ? 'fa-file-text' : 'fa-puzzle-piece'} me-2`}></i>
                      {page?.pageType === 'pagina' ? 'Pagina' : 'Component'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fa fa-bolt me-2"></i>
                    Snelle Acties
                  </h6>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setActiveTab('seo')}
                    >
                      <i className="fa fa-search me-2"></i>
                      SEO Optimaliseren
                    </button>
                    <button 
                      className="btn btn-outline-info btn-sm"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      <i className="fa fa-eye me-2"></i>
                      Voorvertoning
                    </button>
                    <button className="btn btn-outline-secondary btn-sm">
                      <i className="fa fa-copy me-2"></i>
                      Dupliceren
                    </button>
                    <button className="btn btn-outline-warning btn-sm">
                      <i className="fa fa-history me-2"></i>
                      Geschiedenis
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  page: state.cms.pages.item,
  settings: state.cms.settings
});

export default connect(mapStateToProps, {
  getonepages,
  savepages,
  message
})(ModernContentEditor);